/**
 * M7 S6: the data-driven mission runtime (spec §3, §13 task 8; plan §2 "Mission runtime").
 *
 * Missions are step machines in `src/data/story/missions/*.json`. The Career's `story.missions[]` is the truth:
 * `{ id, step, state, choices, awaitingFork }`, absent = not offered yet.
 *
 * - **Offers** (`evaluateOffers`) open on the story tier and the offer gates only (flags, standing, thread weight),
 *   never on "previous mission done" (spec §3: missions 1–4 run in parallel where their offers allow).
 * - **Steps** (`stepMission`) advance on `StoryEvent`s (`steps.ts`). A step has **no deadline**: nothing here reads
 *   time, and a contract-type step is a story-owned job (`missionJob`) that never enters `contracts.active`, so no
 *   expiry reaches it (SL-1). If its job fails (`failStep`) it is re-offered; the step stays open.
 * - **Forks** come after a step. The step's completion offers the fork with every choice's cost text
 *   (`missionFork` effect, spec §1 rule 3); `takeFork` is refused until then, checks the choice can be paid, applies
 *   the outcome and asks for an autosave at once (T24).
 * - **Outcomes** write flags, items (through `items.ts`: sealed, sold originals leave the world), standings,
 *   credits, named payouts (the M1 bounty is `leverage.ts`'s, wired by S12), clock alerts, Reyes's and Okafor's
 *   states, worker `StoryCommand`s, a comms line and the echo (a `callback` countdown, S11 handles it).
 *
 * Pure: Career in, Career out. Event-driven, never per frame.
 */
import { withStanding, withWallet } from '../career/ops'
import type { Career } from '../career/types'
import { logger } from '../core/logger'
import { scheduleCountdown } from './countdowns'
import { storyData } from './data'
import { copyItem, grantItem, itemAboard, loseItem, sellItem, setItemContraband, storyItemOf, syncStoryHold, withholdItem } from './items'
import type { EvidenceLog, MissionDef, MissionOutcome } from './schemas'
import { storyOf, withStory } from './state'
import { stepMatches, type MissionStep } from './steps'
import {
  CLOCK_ALERTS,
  MISSION_IDS,
  STORY_FLAGS,
  type MissionId,
  type Speaker,
  type StoryCommand,
  type StoryEffect,
  type StoryEvent,
  type StoryFlags,
  type StoryItem,
  type StoryMission,
  type StoryResult,
  type Thread,
} from './types'

// ---------------------------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------------------------

export type MissionDefs = Readonly<Record<MissionId, MissionDef>>
export type MissionFork = MissionDef['forks'][number]
export type MissionChoice = MissionFork['choices'][number]

/** A named formula payout (`outcome.payout`): Career in, Career out (S12 wires the M1 bounty). */
export type PayoutFn = (career: Career) => Career

export interface MissionContext {
  missions: MissionDefs
  /** Logs for the offers' thread-weight gates. */
  logsById: ReadonlyMap<string, Pick<EvidenceLog, 'thread' | 'weight'>>
  /** The speaker of a comms node (an outcome's `comms` line). */
  commsSpeaker: (nodeId: string) => Speaker | undefined
  /** Online days before an echo fires (`callbacks.json`), undefined = unknown id (fires on the next online day). */
  echoDelay: (echoId: string) => number | undefined
  payouts?: Readonly<Record<string, PayoutFn>>
}

/** The shipped context (story data parsed once). */
export function missionContext(payouts?: Readonly<Record<string, PayoutFn>>): MissionContext {
  const data = storyData()
  const ctx: MissionContext = {
    missions: data.missions,
    logsById: data.logsById,
    commsSpeaker: (nodeId) => data.commsById.get(nodeId)?.speaker,
    echoDelay: (echoId) => data.callbacks.find((c) => c.id === echoId)?.delayDays[0],
  }
  if (payouts !== undefined) ctx.payouts = payouts
  return ctx
}

// ---------------------------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------------------------

export const MISSION_REFUSALS = [
  'unknownMission',
  'notOffered',
  'notActive',
  'noFork',
  'unknownChoice',
  'funds',
  'itemNotAboard',
  'notContractStep',
] as const
export type MissionRefusal = (typeof MISSION_REFUSALS)[number]

export type MissionCmd = { ok: true; result: StoryResult<Career> } | { ok: false; reason: MissionRefusal }

function refuse(reason: MissionRefusal): MissionCmd {
  return { ok: false, reason }
}

/** Accumulates commands and effects while a function threads the Career through several changes. */
export interface OutcomeSink {
  commands: StoryCommand[]
  effects: StoryEffect[]
}

function newOut(): OutcomeSink {
  return { commands: [], effects: [] }
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

export function missionOf(career: Career, id: MissionId): StoryMission | undefined {
  return storyOf(career).missions.find((m) => m.id === id)
}

/** The mission's current step (the next one to complete, or the done one awaiting a fork), if any. */
export function currentStep(career: Career, id: MissionId, ctx: MissionContext): MissionStep | undefined {
  const m = missionOf(career, id)
  if (m === undefined || m.state === 'complete') return undefined
  return ctx.missions[id].steps.find((s) => s.id === m.step)
}

/** The step's story-owned job (S30 creates it): no deadline, never in `contracts.active` (spec §2.8, SL-1). */
export interface MissionJob {
  missionId: MissionId
  stepId: string
  verb: MissionStep['verb']
  target: MissionStep['target']
  contractType?: NonNullable<MissionStep['contractType']>
  item?: NonNullable<MissionStep['item']>
  fallback?: NonNullable<MissionStep['fallback']>
}

export function missionJob(career: Career, id: MissionId, ctx: MissionContext): MissionJob | null {
  const m = missionOf(career, id)
  const step = currentStep(career, id, ctx)
  if (m === undefined || step === undefined || m.awaitingFork !== undefined) return null
  const job: MissionJob = { missionId: id, stepId: step.id, verb: step.verb, target: step.target }
  if (step.contractType !== undefined) job.contractType = step.contractType
  if (step.item !== undefined) job.item = step.item
  if (step.fallback !== undefined) job.fallback = step.fallback
  return job
}

/** The weight of the logs the player holds (read or not) on `thread` (an offer gate, spec §7 `settlement ≥ 1`). */
export function threadWeight(career: Career, thread: Thread, logsById: MissionContext['logsById']): number {
  const story = storyOf(career)
  let w = 0
  for (const list of [story.readLogs, story.unreadLogs]) {
    for (const logId of list) {
      const log = logsById.get(logId)
      if (log !== undefined && log.thread === thread) w += log.weight
    }
  }
  return w
}

export type OfferBlock = 'noSteps' | 'tier' | 'flags' | 'standing' | 'threadWeight'

/** Why the mission can't be offered now, or null. Never reads another mission's state (spec §3). */
export function offerBlock(career: Career, def: MissionDef, ctx: MissionContext): OfferBlock | null {
  if (def.steps.length === 0) return 'noSteps'
  if ((career.storyTier ?? 0) < def.offer.tier) return 'tier'
  const flags = storyOf(career).flags
  if (def.offer.flagsAll.some((f) => flags[f] !== true) || def.offer.flagsNone.some((f) => flags[f] === true)) return 'flags'
  if (def.offer.standing.some((s) => career.standings[s.faction] < s.min)) return 'standing'
  if (def.offer.threadWeight.some((t) => threadWeight(career, t.thread, ctx.logsById) < t.min)) return 'threadWeight'
  return null
}

// ---------------------------------------------------------------------------------------------
// Mission record writes
// ---------------------------------------------------------------------------------------------

function putMission(career: Career, mission: StoryMission): Career {
  return withStory(career, (s) => {
    const at = s.missions.findIndex((m) => m.id === mission.id)
    const missions = at < 0 ? [...s.missions, mission] : s.missions.map((m, i) => (i === at ? mission : m))
    // Keep MISSION_IDS order so equal careers serialize equally.
    missions.sort((a, b) => MISSION_IDS.indexOf(a.id) - MISSION_IDS.indexOf(b.id))
    return { ...s, missions }
  })
}

// ---------------------------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------------------------

/** `play` runs every write; `dev` (dev jumps) writes state only: no money, no payouts, no comms or echoes. */
export type OutcomeMode = 'play' | 'dev'

type OutcomeItem = MissionOutcome['items'][number]

/** Writes an item record directly (dev jumps, or a play write the item helpers refused) and repairs the hold. */
function writeItemRecord(career: Career, o: OutcomeItem): Career {
  const prev = storyItemOf(career, o.id)
  const item: StoryItem = { id: o.id, state: o.state }
  const where = o.where ?? prev?.where
  if (where !== undefined && o.state !== 'held') item.where = where
  const contraband = o.contraband ?? prev?.contraband
  if (contraband === true && o.state !== 'sold' && o.state !== 'lost') item.contraband = true
  const next = withStory(career, (s) => {
    const at = s.items.findIndex((i) => i.id === o.id)
    return { ...s, items: at < 0 ? [...s.items, item] : s.items.map((i, k) => (k === at ? item : i)) }
  })
  return syncStoryHold(next)
}

function applyItem(career: Career, o: OutcomeItem, missionId: MissionId, out: OutcomeSink): Career {
  const where = o.where ?? missionId
  let next: Career | null = null
  if (o.state === 'held') {
    const r = grantItem(career, o.id, o.contraband === undefined ? {} : { contraband: o.contraband })
    out.effects.push(...r.effects)
    next = r.career
  } else if (o.state === 'lost') {
    const r = loseItem(career, o.id)
    out.effects.push(...r.effects)
    next = r.career
  } else {
    const r = o.state === 'withheld' ? withholdItem(career, o.id) : o.state === 'copy' ? copyItem(career, o.id, where) : sellItem(career, o.id, where)
    if (r.ok) {
      out.effects.push(...r.value.effects)
      next = r.value.career
    }
  }
  if (next === null || storyItemOf(next, o.id)?.state !== o.state) {
    logger.warn(`story: ${missionId} outcome item ${o.id} → ${o.state} written to the record directly`)
    next = writeItemRecord(next ?? career, { ...o, where })
  }
  if (o.contraband !== undefined && o.state !== 'sold' && o.state !== 'lost') next = setItemContraband(next, o.id, o.contraband)
  return next
}

function withFlags(flags: StoryFlags, set: readonly string[], clear: readonly string[]): StoryFlags {
  const out: StoryFlags = {}
  for (const f of STORY_FLAGS) {
    if (clear.includes(f)) continue
    if (flags[f] === true || set.includes(f)) out[f] = true
  }
  return out
}

/**
 * Applies one outcome (a step's `onDone` or a fork choice). Exported for the dev jumps; play code goes through
 * `stepMission` and `takeFork`.
 */
export function applyOutcome(
  career: Career,
  outcome: MissionOutcome,
  missionId: MissionId,
  ctx: MissionContext,
  mode: OutcomeMode,
  out: OutcomeSink,
): Career {
  let next = career
  if (outcome.setFlags.length > 0 || outcome.clearFlags.length > 0) {
    next = withStory(next, (s) => ({ ...s, flags: withFlags(s.flags, outcome.setFlags, outcome.clearFlags) }))
  }
  for (const item of outcome.items) next = mode === 'dev' ? writeItemRecord(next, item) : applyItem(next, item, missionId, out)
  for (const s of outcome.standings) next = withStanding(next, s.faction, s.delta)
  if (outcome.alerts.length > 0) {
    next = withStory(next, (s) => {
      const alerts = CLOCK_ALERTS.filter((a) => s.clock.alerts.includes(a) || outcome.alerts.includes(a))
      return { ...s, clock: { ...s.clock, alerts } }
    })
  }
  if (outcome.reyesState !== undefined) {
    const reyesState = outcome.reyesState
    next = withStory(next, (s) => ({ ...s, reyesState }))
  }
  if (outcome.okaforState !== undefined) {
    const okaforState = outcome.okaforState
    next = withStory(next, (s) => ({ ...s, okaforState }))
  }
  out.commands.push(...outcome.commands)
  if (mode === 'dev') return next

  if (outcome.credits !== 0) {
    if (next.wallet + outcome.credits < 0) logger.warn(`story: ${missionId} outcome credits ${outcome.credits} exceed the wallet; skipped`)
    else next = withWallet(next, outcome.credits)
  }
  if (outcome.payout !== undefined) {
    const payout = ctx.payouts?.[outcome.payout]
    if (payout === undefined) logger.warn(`story: ${missionId} payout '${outcome.payout}' is not wired`)
    else next = payout(next)
  }
  if (outcome.comms !== undefined) {
    const speaker = ctx.commsSpeaker(outcome.comms) ?? ctx.missions[missionId].offer.contact
    out.effects.push({ type: 'comms', speaker, nodeId: outcome.comms })
  }
  if (outcome.echo !== undefined) next = scheduleEcho(next, outcome.echo, ctx)
  return next
}

function scheduleEcho(career: Career, echoId: string, ctx: MissionContext): Career {
  return scheduleCountdown(career, { id: `cb:${echoId}`, kind: 'callback', inOnlineDays: ctx.echoDelay(echoId) ?? 0, ref: echoId })
}

/** Why a fork choice can't be paid right now (its money or the item it hands over), or null. */
export function choiceRefusal(career: Career, choice: MissionChoice): MissionRefusal | null {
  const o = choice.outcome
  if (o.credits < 0 && career.wallet + o.credits < 0) return 'funds'
  for (const item of o.items) {
    if (item.state !== 'copy' && item.state !== 'sold' && item.state !== 'withheld') continue
    const rec = storyItemOf(career, item.id)
    if (rec === undefined || rec.state !== 'held' || !itemAboard(rec)) return 'itemNotAboard'
  }
  return null
}

// ---------------------------------------------------------------------------------------------
// Advancing
// ---------------------------------------------------------------------------------------------

function pendingFork(def: MissionDef, mission: StoryMission, stepId: string): MissionFork | undefined {
  return def.forks.find((f) => f.afterStep === stepId && mission.choices?.[f.id] === undefined)
}

/** After the current step is done (and any fork taken): offer the next fork, or move to the next step, or finish. */
function advance(career: Career, def: MissionDef, mission: StoryMission, ctx: MissionContext, out: OutcomeSink): Career {
  const fork = pendingFork(def, mission, mission.step)
  if (fork !== undefined) {
    out.effects.push({
      type: 'missionFork',
      missionId: def.id,
      forkId: fork.id,
      choices: fork.choices.map((c) => ({ id: c.id, cost: c.cost })),
    })
    return putMission(career, { ...mission, awaitingFork: fork.id })
  }
  const at = def.steps.findIndex((s) => s.id === mission.step)
  const nextStep = def.steps[at + 1]
  const base: StoryMission = { ...mission }
  delete base.awaitingFork
  if (nextStep !== undefined) {
    out.effects.push({ type: 'missionStep', missionId: def.id, stepId: nextStep.id, reoffer: false })
    out.effects.push({ type: 'autosave', reason: `mission:${def.id}:${nextStep.id}` })
    return putMission(career, { ...base, step: nextStep.id })
  }
  out.effects.push({ type: 'missionComplete', missionId: def.id })
  out.effects.push({ type: 'autosave', reason: `mission:${def.id}:complete` })
  let next = putMission(career, { ...base, state: 'complete' })
  if (def.echo !== undefined) next = scheduleEcho(next, def.echo, ctx)
  return next
}

/** Offers every mission whose offer gates are met and that isn't on record yet. */
export function evaluateOffers(career: Career, ctx: MissionContext): StoryResult<Career> {
  let next = career
  const effects: StoryEffect[] = []
  for (const id of MISSION_IDS) {
    if (missionOf(next, id) !== undefined) continue
    const def = ctx.missions[id]
    if (offerBlock(next, def, ctx) !== null) continue
    const first = def.steps[0]
    next = putMission(next, { id, step: first.id, state: 'offered' })
    effects.push({ type: 'missionOffered', missionId: id, contact: def.offer.contact })
    if (def.offer.comms !== undefined) effects.push({ type: 'comms', speaker: def.offer.contact, nodeId: def.offer.comms })
  }
  return { career: next, commands: [], effects }
}

/** The player takes an offered mission: its first step's job is created (no deadline). */
export function acceptMission(career: Career, id: MissionId, ctx: MissionContext): MissionCmd {
  const m = missionOf(career, id)
  if (m === undefined || m.state !== 'offered' || currentStep(career, id, ctx) === undefined) return refuse('notOffered')
  const next = putMission(career, { ...m, state: 'active' })
  return {
    ok: true,
    result: {
      career: next,
      commands: [],
      effects: [
        { type: 'missionStep', missionId: id, stepId: m.step, reoffer: false },
        { type: 'autosave', reason: `mission:${id}:accepted` },
      ],
    },
  }
}

/**
 * Advances every offered or active mission whose current step `event` completes (one step per mission per event;
 * an offered mission whose first step the player just did becomes active), then evaluates offers.
 */
export function stepMission(career: Career, event: StoryEvent, ctx: MissionContext): StoryResult<Career> {
  let next = career
  const out = newOut()
  for (const id of MISSION_IDS) {
    const m = missionOf(next, id)
    if (m === undefined || m.state === 'complete' || m.awaitingFork !== undefined) continue
    const def = ctx.missions[id]
    const step = def.steps.find((s) => s.id === m.step)
    if (step === undefined || !stepMatches(next, step, event)) continue
    if (m.state === 'offered') next = putMission(next, { ...m, state: 'active' })
    if (step.onDone !== undefined) next = applyOutcome(next, step.onDone, id, ctx, 'play', out)
    const now = missionOf(next, id)
    if (now !== undefined) next = advance(next, def, now, ctx, out)
  }
  const offers = evaluateOffers(next, ctx)
  return { career: offers.career, commands: out.commands, effects: [...out.effects, ...offers.effects] }
}

/** Commits a fork choice (only after its costs were shown: the mission is awaiting this fork). Autosaves at once. */
export function takeFork(career: Career, id: MissionId, forkId: string, choiceId: string, ctx: MissionContext): MissionCmd {
  const m = missionOf(career, id)
  if (m === undefined) return refuse('unknownMission')
  if (m.awaitingFork !== forkId) return refuse('noFork')
  const def = ctx.missions[id]
  const fork = def.forks.find((f) => f.id === forkId)
  const choice = fork?.choices.find((c) => c.id === choiceId)
  if (fork === undefined || choice === undefined) return refuse('unknownChoice')
  const why = choiceRefusal(career, choice)
  if (why !== null) return refuse(why)

  const out = newOut()
  let next = applyOutcome(career, choice.outcome, id, ctx, 'play', out)
  out.effects.push({ type: 'autosave', reason: `fork:${id}:${forkId}:${choiceId}` })
  const after = missionOf(next, id) ?? m
  const chosen: StoryMission = { ...after, choices: { ...after.choices, [forkId]: choiceId } }
  delete chosen.awaitingFork
  next = advance(putMission(next, chosen), def, chosen, ctx, out)
  const offers = evaluateOffers(next, ctx)
  return {
    ok: true,
    result: { career: offers.career, commands: out.commands, effects: [...out.effects, ...offers.effects] },
  }
}

/**
 * A contract-type step's job failed (the cargo was lost, the job abandoned): the step stays open and its job is
 * offered again (spec §13 task 8). Steps never fail on time: there is no deadline.
 */
export function failStep(career: Career, id: MissionId, ctx: MissionContext): MissionCmd {
  const m = missionOf(career, id)
  if (m === undefined || m.state !== 'active' || m.awaitingFork !== undefined) return refuse('notActive')
  const step = currentStep(career, id, ctx)
  if (step?.contractType === undefined) return refuse('notContractStep')
  return {
    ok: true,
    result: { career, commands: [], effects: [{ type: 'missionStep', missionId: id, stepId: step.id, reoffer: true }] },
  }
}
